/* -------------------------- Design imports start -------------------------- */
import { Box, Card, IconButton, Modal, Typography } from '@mui/material'
import ImageEditor from './ImageEditor'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import Button from '../Button'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FileInfo } from '../../utils/types'
import LogTool from '../../logger/logTools'
import { toast } from 'react-toastify'
import DiscardChangesModal from '../widgets/DiscardChangesModal'
/* ------------------------- Functional imports end ------------------------- */

interface Props {
  open: boolean
  setOpen: (open: boolean) => void
  title?: string
  file?: FileInfo // the file to edit. We use the document property of the FileInfo object to download the files contents
  confirmButtonProps: {
    text?: string
    onConfirm: (
      filename: string,
      exportedImage: Blob
    ) => Promise<'SUCCESS' | 'ERROR'> | 'SUCCESS' | 'ERROR'
    disabled: boolean
  }
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function ImageManipulationModal(props: Props): ReactElement {
  /* ------------------------- Non state data start ------------------------- */
  const { open, setOpen, title, file, confirmButtonProps } = props
  const { t } = useTranslation()
  const log = new LogTool({ context: 'ImageManipulationModal', enable: true, logLevel: 'debug' })
  /* -------------------------- Non state data end -------------------------- */

  /* --------------------------- Flag states start -------------------------- */
  // every time this state changes to a number that evaluates with Boolean(trigger) === true
  // a new image export is triggered
  const [triggerExportImage, setTriggerExportImage] = useState<number | undefined>(undefined)
  const [openDiscardChangesModal, setOpenDiscardChangesModal] = useState(false)
  /* ---------------------------- Flag states end --------------------------- */

  /* ----------------------- Callback functions start ----------------------- */
  const handleClose = (): void => {
    // reset modal states
    setTriggerExportImage(undefined)
    setOpen(false)
  }
  const handleConfirm = (): void => {
    // prepare to call onConfirm by requesting the manipulated image data url
    // Note: this triggers the onExportImage callback to be executed
    setTriggerExportImage(prev => {
      // increase the trigger counter to make an effective state change
      if (prev) return prev + 1
      return 1
    })
  }
  const handleExportImage = async (filename: string, exportedImage: Blob): Promise<void> => {
    // image blob is available -> call onConfirm callback
    const confirmStatus = await confirmButtonProps.onConfirm(filename, exportedImage)
    if (confirmStatus === 'SUCCESS') {
      toast.success(t('imageManipulation:feedback.success.exportingImage'))
      // close modal
      handleClose()
    } else {
      toast.error(t('imageManipulation:feedback.error.exportingImage'))
    }
  }
  /* ------------------------ Callback functions end ------------------------ */

  /* ------------------------------------------------------------------------ */
  /*                             Render component                             */
  /* ------------------------------------------------------------------------ */
  return (
    <>
      <Modal open={open} onClose={() => setOpenDiscardChangesModal(true)}>
        <Card
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            height: '90vh',
            width: '80vw',
            outline: 'none',
          }}
        >
          <Box
            id="image-manipulation-modal-content-container"
            display="flex"
            flexDirection="column"
            height="100%"
          >
            <Box
              id="image-manipulation-modal-header"
              display="flex"
              justifyContent="space-between"
              padding="16px"
            >
              <Typography variant="h6">
                {title && title !== ''
                  ? title
                  : t('imageManipulation:content.heading.imageManipulation')}
              </Typography>
              <IconButton onClick={() => setOpenDiscardChangesModal(true)}>
                <CloseRoundedIcon />
              </IconButton>
            </Box>
            <Box
              id="image-manipulation-modal-content"
              flexGrow={1}
              padding="16px"
              sx={{
                overflowY: 'auto',
              }}
            >
              <ImageEditor
                file={file}
                onExportImageAvailable={handleExportImage}
                triggerExportImage={triggerExportImage}
              />
            </Box>
            <Box
              id="image-manipulation-modal-actions"
              display="flex"
              justifyContent="space-between"
              padding="16px"
            >
              <Button
                variant="contained"
                color="inherit"
                onClick={() => setOpenDiscardChangesModal(true)}
              >
                {t('common:interaction.button.cancel')}
              </Button>
              <Button variant="contained" onClick={handleConfirm}>
                {confirmButtonProps.text || t('imageManipulation:interaction.button.applyChanges')}
              </Button>
            </Box>
          </Box>
        </Card>
      </Modal>
      <DiscardChangesModal
        open={openDiscardChangesModal}
        setOpen={setOpenDiscardChangesModal}
        confirmButtonProps={{
          onConfirm: handleClose,
          disabled: false,
        }}
      />
    </>
  )
}
